// CMSFeaturesPage.tsx v2.1 (Force Update)
import React, { useState, FormEvent } from 'react';
import '@/assets/css/cms-features.css';

const cmsFeatures = [
  {
    id: 1,
    title: 'リモート一括管理',
    description: 'インターネット経由で複数のディスプレイを一括管理。場所を問わず、PCやタブレットから簡単に操作できます。',
    image: '/org/cms/cms_system.png',
    points: [
      '複数拠点の一元管理',
      'リアルタイム稼働監視',
      '遠隔トラブルシューティング',
      'マルチユーザー・権限管理',
    ],
  },
  {
    id: 2,
    title: '柔軟なスケジュール管理',
    description: '時間帯、曜日に合わせたコンテンツの自動配信。輝度調整のスケジュールで省エネに寄与します。',
    image: '/org/cms/cms_schedule.jpg',
    points: [
      '時間帯別コンテンツ配信',
      '輝度調整のスケジュールで省エネに寄与',
      '緊急時優先配信',
    ],
  },
  {
    id: 3,
    title: '直感的なコンテンツエディター',
    description: '専門知識不要のドラッグ&ドロップ操作で、魅力的なデジタルコンテンツを作成。豊富なテンプレートも用意されています。',
    image: '/org/cms/cms_layout.jpg',
    points: [
      'ドラッグ&ドロップエディター',
      '豊富なテンプレート',
      'URL（Webページ）表示',
      '動画・画像・音声',
      'テキスト対応',
      'リアルタイムプレビュー',
    ],
  },
];

const capabilities = [
  {
    icon: '🎥',
    title: '動画コンテンツ',
    description: 'MP4の主要動画フォーマットに対応。4K高画質動画も滑らかに再生可能。',
  },
  {
    icon: '🖼️',
    title: '画像・写真',
    description: 'JPEG、PNGの画像形式に対応。スライドショー機能で効果的な表示が可能。',
  },
  {
    icon: '📄',
    title: 'テキスト・文書',
    description: 'リアルタイムテキストテロップ。',
  },
  {
    icon: '🌐',
    title: 'Webページ',
    description: 'ウェブサイトやアプリケーションの直接表示。',
  },
];

const benefits = [
  {
    number: 1,
    title: '運用コスト削減',
    description: '一元管理による効率化で人的リソースを他の業務に集中できます。',
  },
  {
    number: 2,
    title: '迅速な情報更新',
    description: '緊急時や突発的な情報変更にも即座に対応。リアルタイムでの情報発信が可能。',
  },
  {
    number: 3,
    title: 'スケーラビリティ',
    description: '1台から数千台まで、規模に応じた柔軟な拡張が可能。成長に合わせてシステムを拡張できます。',
  },
];

export const CMSFeaturesPage: React.FC = () => {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/mail.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      alert('お問い合わせありがとうございます。担当者より折り返しご連絡いたします。');
      // フォームをリセット
      setFormData({
        company: '',
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (err) {
      setError('送信に失敗しました。しばらくしてからもう一度お試しください。');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <>
      {/* CMSヒーローセクション */}
      <section className="cms-hero">
        <div className="cms-hero-image">
          <img src="/org/cms/cms_top.png" alt="WiCanvas CMSトップ画面" />
        </div>
      </section>

      {/* CMS概要 */}
      <section id="overview" className="cms-overview">
        <div className="container">
          <div className="cms-overview-content">
            <div className="cms-overview-text">
              <h2>
                プロフェッショナルな
                <br />
                デジタルサイネージ管理
              </h2>
              <p>
                WiCanvas
                CMSは、企業のデジタルサイネージ運用を革新するクラウドベースの管理システムです。直感的な操作画面で、コンテンツの作成から配信、スケジュール管理まで、すべてを一元管理できます。
              </p>
              <p>
                Wistronの先進技術とIndustrial
                PC技術を組み合わせた総合ソリューションとして、世界中のお客様にプロフェッショナルディスプレイ、メディアプレーヤー、クラウドソフトウェア、そして地域密着型のサービスを提供します。
              </p>
            </div>
            <div className="cms-overview-image">
              <img src="/org/cms/cms_smartphone.png" alt="WiCanvas CMS管理画面" />
            </div>
          </div>
        </div>
      </section>

      {/* CMS主要機能 */}
      <section className="cms-features">
        <div className="container">
          <h2 className="section-title">主要機能</h2>

          {cmsFeatures.map((feature) => (
            <div key={feature.id} className="cms-feature">
              <div className="cms-feature-content">
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                <ul className="feature-points">
                  {feature.points.map((point, idx) => (
                    <li key={idx}>{point}</li>
                  ))}
                </ul>
              </div>
              <div className="cms-feature-image">
                <img src={feature.image} alt={feature.title} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CMS対応機能 */}
      <section className="cms-capabilities">
        <div className="container">
          <h2 className="section-title">対応メディア・機能</h2>
          <p className="section-description">
            幅広いメディアフォーマットと豊富な機能で、あらゆるコンテンツニーズに対応
          </p>

          <div className="capabilities-grid">
            {capabilities.map((capability, index) => (
              <div key={index} className="capability-card">
                <div className="capability-icon">{capability.icon}</div>
                <h4>{capability.title}</h4>
                <p>{capability.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CMSの効果 */}
      <section className="cms-benefits">
        <div className="container">
          <h2 className="section-title">WiCanvas CMSの効果</h2>
          <p className="section-description">
            デジタルサイネージ運用の効率化と効果的なコミュニケーションを実現
          </p>

          <div className="benefits-grid">
            {benefits.map((benefit) => (
              <div key={benefit.number} className="benefit-card">
                <div className="benefit-number">{benefit.number}</div>
                <h4>{benefit.title}</h4>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* お問い合わせセクション */}
      <section id="contact" className="contact">
        <div className="container">
          <h2 className="section-title">CMS機能についてのお問い合わせ</h2>
          <div className="contact-content">
            <div className="contact-info">
              <h3>WiCanvas CMSの詳細はお気軽にご相談ください</h3>
              <p>
                お客様の業務要件に最適なCMS機能や、導入後のサポートまで、専門スタッフが丁寧に対応いたします。デモンストレーションもご用意しています。
              </p>
            </div>
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="company">会社名</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">
                    お名前 <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">
                    メールアドレス <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="phone">電話番号</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="subject">お問い合わせ種別</label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                >
                  <option value="">選択してください</option>
                  <option value="CMSデモンストレーション">CMSデモンストレーション</option>
                  <option value="CMS機能について">CMS機能について</option>
                  <option value="カスタム開発">カスタム開発</option>
                  <option value="システム連携">システム連携</option>
                  <option value="お見積り">お見積り</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="message">
                  お問い合わせ内容 <span className="required">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="想定している利用シーン、管理したいディスプレイ台数、必要な機能などをお聞かせください。"
                />
              </div>
              <button type="submit" className="btn btn-primary btn-large" disabled={isSubmitting}>
                {isSubmitting ? '送信中...' : '送信する'}
              </button>
              {error && <p className="error-message">{error}</p>}
            </form>
          </div>
        </div>
      </section>
    </>
  );
};
